import { useEffect, useRef, useState } from "react";
import { useConnection, usePublicClient, useWalletClient } from "wagmi";
import { base } from "wagmi/chains";
import type { Address, WalletClient } from "viem";
import { createV2PaidLaunchClient, type V2PaidLaunchConsent, type V2PaidLaunchStore } from "./v2-paid-launch";
import { createV2PaidLaunchIndexedDbStore } from "./v2-paid-launch-store";
import { createV2PaidLaunchBrowserPorts, type V2PaidLaunchBrowserPorts } from "./v2-paid-launch-browser";
import { findPurchaseCandidate, verifyRefundedTerminalPurchase } from "./v2-launch-recovery";
import { createV2BudgetFundingClient, V2BudgetFundingError, type BudgetVaultReference } from "./v2-budget-funding";
import { isVerifiedRetiredUnusedPurchaseAction, hasVerifiedTerminalOnchainHistory } from "./v2-retired-unused";
import type { DeploymentReference, SystemPrompt } from "./public-v2";
import { WalletReservationError, type WalletOperation, type WalletReservationRecoveryRecord, type WalletSessionDescriptor } from "./wallet-auth";
import type { WalletSessionUi } from "./use-wallet-session";
import { advanceLaunchActivity, createLaunchActivity, finishLaunchActivity, setLaunchActivityVisible,
  type LaunchActionName, type LaunchActivitySnapshot, type LaunchActivityStatus, type WalletProgressCode } from "./launch-activity";

const GENERIC_ERROR = "The paid launch could not be completed. Refresh its status before trying again.";
const UNCERTAIN_ERROR = "The paid launch may still be processing. Reconcile its status before another transaction.";
function fundingReconciliationMessage(error: unknown): string {
  if (!(error instanceof V2BudgetFundingError)) return "Funding reconciliation did not verify canonical evidence. The saved funding intent remains unresolved; check status before trying another action.";
  switch (error.code) {
    case "unresolved": return "Funding reconciliation is awaiting canonical transaction evidence. The saved intent remains unresolved; check status before another action.";
    case "mismatch": return "Funding evidence did not match the saved intent. The saved intent remains unresolved for review; no evidence was cleared.";
    case "unavailable": return "Funding status could not be read. The saved intent remains unresolved; check status again later.";
    case "timeout": return "Funding status check timed out. The saved intent remains unresolved; check status again later.";
    case "conflict": return "Saved funding state changed during reconciliation. The current intent remains unresolved; refresh status before proceeding.";
    case "cancelled": return "Funding reconciliation was cancelled. The saved intent remains unresolved; check status before another action.";
    case "invalid_input": return "The saved funding intent could not be validated. It remains unresolved for review.";
    default: return "Funding reconciliation did not verify canonical evidence. The saved funding intent remains unresolved; check status before trying another action.";
  }
}
const ADDRESS = /^0x[0-9a-f]{40}$/i;
const QUOTE_DIAGNOSTIC_NAMESPACE = "botnet.launch.quote";
type QuoteDiagnosticCode = "disabled" | "invalid_request" | "unauthorized" | "forbidden" | "expired" | "invalid_signature" |
  "quota" | "conflict" | "not_found" | "unavailable" | "invalid_response" | "timeout" | "cancelled" |
  "invalid_input" | "mismatch" | "unresolved" | "unknown";
const QUOTE_ERROR_CODES: ReadonlySet<Exclude<QuoteDiagnosticCode, "unknown">> = new Set([
  "disabled", "invalid_request", "unauthorized", "forbidden", "expired", "invalid_signature", "quota", "conflict",
  "not_found", "unavailable", "invalid_response", "timeout", "cancelled", "invalid_input", "mismatch", "unresolved",
]);
type QuoteDiagnosticPhase = "action_start" | "action_blocked" | "action_error" | "recovery_start" | "recovery_ready" |
  "recovery_error" | "selected_read_start" | "selected_read_ok" | "selected_read_error" | "submit_start" | "submit_error" | "submit_ok" |
  "financial_action_error";
type QuoteDiagnosticReason = "no_binding" | "disabled" | "busy" | "recovery_unavailable" | "selection_invalid" |
  "readiness" | "record_not_quoteable" | "readiness_changed";
type QuoteDiagnosticFields = Readonly<{ code?: QuoteDiagnosticCode; reason?: QuoteDiagnosticReason }>;
function quoteErrorCode(error: unknown): QuoteDiagnosticCode {
  try {
    if ((typeof error !== "object" && typeof error !== "function") || error === null) return "unknown";
    const descriptor = Object.getOwnPropertyDescriptor(error, "code");
    if (!descriptor || !("value" in descriptor) || typeof descriptor.value !== "string" ||
        !QUOTE_ERROR_CODES.has(descriptor.value as Exclude<QuoteDiagnosticCode, "unknown">)) return "unknown";
    return descriptor.value as QuoteDiagnosticCode;
  } catch { return "unknown"; }
}
function reviewErrorText(error: unknown): string {
  try {
    if (typeof error !== "object" || error === null) return GENERIC_ERROR;
    const code = Object.getOwnPropertyDescriptor(error, "code");
    const reason = Object.getOwnPropertyDescriptor(error, "reviewReason");
    if (!code || !("value" in code) || code.value !== "mismatch" || !reason || !("value" in reason)) return GENERIC_ERROR;
    switch (reason.value) {
      case "expired": return "This offer has expired. Request a new quote.";
      case "not_yet_valid": return "This offer is not valid yet.";
      case "insufficient_balance": return "Your USDC balance is too low for this offer.";
      default: return GENERIC_ERROR;
    }
  } catch { return GENERIC_ERROR; }
}
function quoteDiagnostic(phase: QuoteDiagnosticPhase, fields: QuoteDiagnosticFields = {}): void {
  try {
    const payload = { phase, ...(fields.code === undefined ? {} : { code: fields.code }),
      ...(fields.reason === undefined ? {} : { reason: fields.reason }) };
    console.debug(QUOTE_DIAGNOSTIC_NAMESPACE, payload);
  } catch { /* Diagnostic output must not affect a paid-launch action. */ }
}

type DraftInput = Readonly<{ tierId: number | null; name: string; additionalPrompt: string }>;
type Options = Readonly<{
  deployment: DeploymentReference | null;
  wallet: WalletSessionUi | null;
  systemPrompt: SystemPrompt | null;
  draft: DraftInput;
  apiOrigin: string | null;
  budgetVaultReference?: BudgetVaultReference | null;
}>;
type RecordValue = Record<string, unknown>;
type RecoveryStore = V2PaidLaunchStore & {
  listForPayer(payer: string): Promise<readonly RecordValue[]>;
  loadSelected(payer: string): Promise<RecordValue | null>;
  selectAttempt(attemptId: string): Promise<RecordValue>;
};
type IntentEngine = Engine & {
  createDraft(input: Parameters<Engine["createDraft"]>[0], operation: WalletOperation,
    options?: { intent: "new" }): ReturnType<Engine["createDraft"]>;
};
type Review = Readonly<{ observation: unknown; consent: V2PaidLaunchConsent }>;
type Engine = ReturnType<typeof createV2PaidLaunchClient>;
type Binding = {
  key: string;
  deployment: DeploymentReference;
  account: Address;
  store: RecoveryStore & { close?: () => void };
  ports: V2PaidLaunchBrowserPorts;
  engine: IntentEngine;
  funding: ReturnType<typeof createV2BudgetFundingClient>;
  controller: AbortController;
  disposed: boolean;
  recoveryEpoch: number;
  selectedAttemptId: string | null;
  refundedTerminalProofs: Set<string>;
};
type BusyClaim = { binding: Binding; token: object; action: string; activityId?: string };
type ActionProgress = Readonly<{
  stage(code: WalletProgressCode): void;
  finish(status: Exclude<LaunchActivityStatus, "running">, message?: string): void;
}>;
type UiState = {
  binding: Binding | null;
  record: RecordValue | null;
  review: Review | null;
  acknowledgedKey: string | null;
  restoring: boolean;
  busy: BusyClaim | null;
  error: string | null;
  uncertainAttemptId: string | null;
  restoreFailed: boolean;
  fundingStatus: RecordValue | null;
  localAttempts: RecordValue[];
  remoteReservations: WalletReservationRecoveryRecord[];
  recoveryState: "checking" | "ready" | "unavailable";
  selectedAttemptId: string | null;
  recoveryConflict: boolean;
};
type ActionName = LaunchActionName;

export type PaidLaunchView = Readonly<{
  record: RecordValue | null;
  activity: LaunchActivitySnapshot | null;
  review: Review | null;
  acknowledged: boolean;
  restoring: boolean;
  busy: boolean;
  error: string | null;
  enabled: Readonly<Record<ActionName, boolean>>;
  localAttempts: readonly RecordValue[];
  remoteReservations: readonly WalletReservationRecoveryRecord[];
  recoveryState: "checking" | "ready" | "unavailable";
  selectedAttemptId: string | null;
  requestQuote(): Promise<void>;
  reviewQuote(): Promise<void>;
  approve(): Promise<void>;
  purchase(): Promise<void>;
  reconcile(): Promise<void>;
  uploadNext(): Promise<void>;
  closeExpired(): Promise<void>;
  discardDraft(): Promise<void>;
  selectAttempt(attemptId: string): Promise<void>;
  startAnother(): Promise<void>;
  refreshRecovery(): Promise<void>;
  fundingConfigured: boolean;
  fundingStatus: RecordValue | null;
  prepareFunding(amount: string): Promise<void>;
  depositFunding(): Promise<void>;
  reconcileFunding(): Promise<void>;
  readFundingStatus(): Promise<void>;
  refundFunding(): Promise<void>;
  setAcknowledged(value: boolean): void;
  minimizeActivity(): void;
  restoreActivity(): void;
  dismissActivity(): void;
}>;

const objectIds = new WeakMap<object, number>();
let nextObjectId = 1;
function objectId(value: object | null | undefined): string {
  if (!value) return "none";
  let id = objectIds.get(value);
  if (id === undefined) { id = nextObjectId++; objectIds.set(value, id); }
  return String(id);
}
function sameDeployment(left: DeploymentReference | null | undefined, right: DeploymentReference | null | undefined): boolean {
  if (!left || !right) return false;
  return left.schemaVersion === right.schemaVersion && left.protocol === right.protocol && left.chainId === right.chainId &&
    left.registryAddress.toLowerCase() === right.registryAddress.toLowerCase() &&
    left.deploymentBlockNumber === right.deploymentBlockNumber && left.deploymentBlockHash.toLowerCase() === right.deploymentBlockHash.toLowerCase() &&
    left.deploymentTransactionHash.toLowerCase() === right.deploymentTransactionHash.toLowerCase() &&
    left.registryRuntimeCodeHash.toLowerCase() === right.registryRuntimeCodeHash.toLowerCase() && left.sourceCommitment === right.sourceCommitment;
}
function consentKey(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const consent = value as Partial<V2PaidLaunchConsent>;
  return typeof consent.attemptId === "string" && typeof consent.draftDigest === "string" && typeof consent.quoteDigest === "string"
    ? `${consent.attemptId}:${consent.draftDigest.toLowerCase()}:${consent.quoteDigest.toLowerCase()}` : null;
}
function sameData(left: unknown, right: unknown): boolean {
  try { return JSON.stringify(left) === JSON.stringify(right); } catch { return false; }
}
function hasUnresolvedDispatch(record: RecordValue): boolean {
  if (record.activeAction !== null && record.activeAction !== undefined) return true;
  if (Array.isArray(record.actionHistory) && record.actionHistory.some((entry) => {
    if (!isRecord(entry) || (entry.kind !== "approve" && entry.kind !== "purchase") || entry.enteredDispatch !== true) return false;
    if (entry.status === "dispatching" || entry.status === "uncertain") return !isVerifiedRetiredUnusedPurchaseAction(record, entry);
    if (entry.status !== "submitted") return false;
    if (entry.kind === "purchase" && isVerifiedRetiredUnusedPurchaseAction(record, entry)) return false;
    const evidence = entry.kind === "approve" ? record.approvalEvidence : record.paymentEvidence;
    return !(isRecord(evidence) && typeof evidence.transactionHash === "string" && evidence.transactionHash === entry.returnedHash);
  })) return true;
  if (isRecord(record.budgetFunding)) {
    if (record.budgetFunding.activeAction !== null && record.budgetFunding.activeAction !== undefined) return true;
    if (["approval_pending", "deposit_pending", "refund_pending", "uncertain"].includes(String(record.budgetFunding.status))) return true;
  }
  return false;
}
function matchesReservation(record: RecordValue, remote: WalletReservationRecoveryRecord): boolean {
  return reservationId(record) === remote.reservationId && isRecord(record.deployment) &&
    sameDeployment(record.deployment as DeploymentReference, remote.deployment);
}
function reservationIdentity(reservation: WalletReservationRecoveryRecord): string {
  const deployment = reservation.deployment;
  return JSON.stringify([reservation.reservationId, deployment.schemaVersion, deployment.protocol, deployment.chainId,
    deployment.registryAddress.toLowerCase(), deployment.deploymentBlockNumber, deployment.deploymentBlockHash.toLowerCase(),
    deployment.deploymentTransactionHash.toLowerCase(), deployment.registryRuntimeCodeHash.toLowerCase(), deployment.sourceCommitment]);
}
function refundedTerminalProofKey(target: Binding, reservation: WalletReservationRecoveryRecord,
  recoveryEpoch: number, session: WalletSessionDescriptor): string {
  return JSON.stringify([reservationIdentity(reservation), target.key, target.account, JSON.stringify(target.deployment),
    recoveryEpoch, objectId(session)]);
}
function isRecord(value: unknown): value is RecordValue { return value !== null && typeof value === "object" && !Array.isArray(value); }
function reservationId(record: RecordValue): string | null {
  const value = isRecord(record.reservation) ? record.reservation.reservationId : null;
  if (typeof value === "string" && /^0x[0-9a-f]{64}$/.test(value)) return value;
  return typeof record.pendingReservationId === "string" && /^0x[0-9a-f]{64}$/.test(record.pendingReservationId)
    ? record.pendingReservationId : null;
}
function isTerminalLocalRecord(record: RecordValue): boolean {
  if (record.phase === "retired_unused" || record.phase === "retired_unsent" || record.phase === "discarded") return true;
  if (record.phase === "terminal_onchain") return hasVerifiedTerminalOnchainHistory(record);
  if (record.phase !== "reservation_pending" || record.reservationClaimed !== true || !isRecord(record.reservation) ||
      record.reservation.kind !== "state" || record.reservation.schemaVersion !== 2 || record.reservation.state !== "released" ||
      typeof record.pendingReservationId !== "string" || !/^0x[0-9a-f]{64}$/.test(record.pendingReservationId) ||
      record.reservation.reservationId !== record.pendingReservationId || record.activeAction != null ||
      !Array.isArray(record.actionHistory) || record.actionHistory.length !== 0 || isRecord(record.budgetFunding) ||
      record.consent != null || record.approvalEvidence != null || record.paymentEvidence != null) return false;
  return true;
}
function validRecoverySnapshot(value: unknown, payer: string): value is { schemaVersion: 2; records: WalletReservationRecoveryRecord[] } {
  if (!isRecord(value) || value.schemaVersion !== 2 || !Array.isArray(value.records) || value.records.length > 100) return false;
  const seen = new Set<string>();
  for (const item of value.records) {
    if (!isRecord(item) || typeof item.reservationId !== "string" || !/^0x[0-9a-f]{64}$/.test(item.reservationId) ||
        seen.has(item.reservationId) || item.payer !== payer || !Number.isSafeInteger(item.createdAtMs) ||
        !["signing", "quoted", "paid", "reconciling", "released", "converted"].includes(String(item.status)) ||
        !["configured_deployment", "foreign_deployment"].includes(String(item.scope)) || !isRecord(item.deployment)) return false;
    const identity = reservationIdentity(item as unknown as WalletReservationRecoveryRecord);
    if (seen.has(identity)) return false;
    seen.add(identity);
  }
  return true;
}

export function usePaidLaunch(options: Options): PaidLaunchView {
  const selected = useConnection();
  const walletQuery = useWalletClient({ chainId: base.id, connector: selected.connector });
  const publicClient = usePublicClient({ chainId: base.id });
  const walletClient = walletQuery.data as WalletClient | undefined;

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const identityKeyRef = useRef("");
  const bindingRef = useRef<Binding | null>(null);
  const busyRef = useRef<BusyClaim | null>(null);
  const activityRef = useRef<LaunchActivitySnapshot | null>(null);
  const activityCounterRef = useRef(0);
  const [activity, setActivity] = useState<LaunchActivitySnapshot | null>(null);
  const [state, setState] = useState<UiState>({ binding: null, record: null, review: null, acknowledgedKey: null,
    restoring: false, busy: null, error: null, uncertainAttemptId: null, restoreFailed: false, fundingStatus: null,
    localAttempts: [], remoteReservations: [], recoveryState: "unavailable", selectedAttemptId: null, recoveryConflict: false });

  const deployment = options.deployment;
  const account = selected.address?.toLowerCase();
  const suppliedConnection = options.wallet?.connection;
  const connectionMatches = !!account && ADDRESS.test(account) && selected.status === "connected" && selected.chainId === base.id &&
    suppliedConnection?.status === "connected" && suppliedConnection.chainId === base.id &&
    suppliedConnection.address?.toLowerCase() === account;
  const selectedClientsMatch = !!walletClient && !!publicClient && walletClient.chain?.id === base.id &&
    walletClient.account?.address?.toLowerCase() === account && publicClient.chain?.id === base.id;
  const deploymentKey = deployment ? JSON.stringify(deployment) : "unavailable";
  const budgetVaultReference = options.budgetVaultReference ?? null;
  const budgetVaultKey = budgetVaultReference ? JSON.stringify(budgetVaultReference) : "unavailable";
  const connectorKey = selected.connector ? `${selected.connector.id}:${selected.connector.uid}:${objectId(selected.connector)}` : "none";
  const sessionKey = objectId(options.wallet?.session ?? null);
  const identityKey = deployment && options.apiOrigin && connectionMatches && selectedClientsMatch
    ? [deploymentKey, budgetVaultKey, account, base.id, connectorKey, objectId(walletClient), objectId(publicClient), sessionKey, options.apiOrigin].join("|") : "unavailable";
  identityKeyRef.current = identityKey;

  useEffect(() => {
    if (!deployment || identityKey === "unavailable" || !account || !walletClient || !publicClient || !options.apiOrigin) {
      bindingRef.current = null;
      activityRef.current = null;
      setActivity(null);
      setState({ binding: null, record: null, review: null, acknowledgedKey: null, restoring: false, busy: null,
        error: null, uncertainAttemptId: null, restoreFailed: false, fundingStatus: null, localAttempts: [], remoteReservations: [],
        recoveryState: "unavailable", selectedAttemptId: null, recoveryConflict: false });
      return;
    }

    let binding: Binding;
    try {
      const store = createV2PaidLaunchIndexedDbStore();
      const controller = new AbortController();
      let createdBinding: Binding | null = null;
      const ports = createV2PaidLaunchBrowserPorts({ deployment, apiOrigin: options.apiOrigin,
        selectedAccount: account as Address, walletClient, publicClient,
        isCurrent: () => !!createdBinding && !createdBinding.disposed && !controller.signal.aborted &&
          bindingRef.current === createdBinding && identityKeyRef.current === identityKey });
      const reservations = {
        reservationAttempt(input: Parameters<WalletSessionUi["client"]["reservationAttempt"]>[0]) {
          if (!createdBinding || !isBindingCurrent(createdBinding)) throw new Error(GENERIC_ERROR);
          const client = optionsRef.current.wallet?.client;
          if (!client) throw new Error("Wallet session is unavailable.");
          return client.reservationAttempt(input);
        },
      };
      const engine = createV2PaidLaunchClient({ store, wallet: ports.wallet, rpc: ports.rpc, lookup: ports.lookup, reservations });
      const funding = createV2BudgetFundingClient({ store, paid: engine, rpc: ports.rpc, wallet: ports.wallet,
        reference: budgetVaultReference, parentDeployment: deployment });
      binding = { key: identityKey, deployment, account: account as Address, store: store as RecoveryStore, ports,
        engine: engine as IntentEngine, funding, controller, disposed: false, recoveryEpoch: 0, selectedAttemptId: null,
        refundedTerminalProofs: new Set() };
      createdBinding = binding;
    } catch {
      activityRef.current = null;
      setActivity(null);
      setState({ binding: null, record: null, review: null, acknowledgedKey: null, restoring: false, busy: null,
        error: GENERIC_ERROR, uncertainAttemptId: null, restoreFailed: true, fundingStatus: null, localAttempts: [], remoteReservations: [],
        recoveryState: "unavailable", selectedAttemptId: null, recoveryConflict: false });
      return;
    }
    bindingRef.current = binding;
    activityRef.current = null;
    setActivity(null);
    setState({ binding, record: null, review: null, acknowledgedKey: null, restoring: true, busy: null, error: null,
      uncertainAttemptId: null, restoreFailed: false, fundingStatus: null, localAttempts: [], remoteReservations: [],
      recoveryState: "checking", selectedAttemptId: null, recoveryConflict: false });
    void loadRecovery(binding, false);

    return () => {
      binding.disposed = true;
      binding.controller.abort();
      binding.ports.dispose();
      if (bindingRef.current === binding) bindingRef.current = null;
    };
  }, [identityKey]);

  const stateMatches = state.binding !== null && state.binding === bindingRef.current && state.binding.key === identityKey;
  const binding = stateMatches ? state.binding : null;
  const record = binding ? state.record : null;
  const review = binding ? state.review : null;
  const acknowledged = !!binding && !!review && !!record && state.acknowledgedKey !== null &&
    state.acknowledgedKey === consentKey(review.consent) && review.consent.attemptId === record.attemptId &&
    review.consent.draftDigest === record.draftDigest && review.consent.quoteDigest === (record.consent as RecordValue | null)?.quoteDigest;
  const busy = !!binding && busyRef.current?.binding === binding;
  const currentWallet = options.wallet;
  const session = currentWallet?.session ?? null;
  const capabilities = currentWallet?.capabilities ?? null;
  const sessionMatches = !!binding && !!session && session.schemaVersion === 2 && session.chainId === base.id &&
    session.address.toLowerCase() === binding.account && session.expiresAtMs > Date.now() && session.idleExpiresAtMs > Date.now();
  const readinessMatches = !!binding && sameDeployment(capabilities?.deployment, binding.deployment) &&
    capabilities?.walletAuthentication === true;
  const connected = !!binding && connectionMatches && selectedClientsMatch && account === binding.account;
  const newLaunchReady = !!binding && connected && sessionMatches && readinessMatches && capabilities?.admission === true && capabilities.payments === true;
  const ownedQuoteReady = !!binding && connected && sessionMatches && readinessMatches;
  const hasMatchingReview = acknowledged && !!record && record.phase === "quoted";
  const noUncertainty = !record || state.uncertainAttemptId !== record.attemptId;
  const noEnteredAction = !record || !hasUnresolvedDispatch(record);
  const anyUnresolvedAction = state.localAttempts.some(hasUnresolvedDispatch);
  const serverOnlyUnresolved = state.remoteReservations.some((remote) =>
    ["signing", "quoted", "paid", "reconciling"].includes(remote.status) &&
    !state.localAttempts.some((local) => matchesReservation(local, remote)) &&
    !(remote.status === "paid" && binding && session && binding.refundedTerminalProofs.has(
      refundedTerminalProofKey(binding, remote, binding.recoveryEpoch, session))));
  const recoveryReady = !!binding && state.recoveryState === "ready" && !state.recoveryConflict && !state.restoreFailed;
  const existingAttempt = !!record;
  const retiredIntent = record?.phase === "retired_unused" || record?.phase === "retired_unsent" ||
    (record?.phase === "terminal_onchain" && hasVerifiedTerminalOnchainHistory(record));
  const startableAttempt = !existingAttempt || record?.phase === "draft" || record?.phase === "reservation_pending" || retiredIntent;
  const selectedSavedIntent = !!record && (record.phase === "draft" || record.phase === "reservation_pending");
  const pendingReleased = !!record && record.phase === "reservation_pending" && isRecord(record.reservation) &&
    record.reservation.kind === "state" && record.reservation.state === "released";
  const pendingQuoteRetry = !!record && record.phase === "reservation_pending" && !pendingReleased && ownedQuoteReady && noUncertainty && noEnteredAction;
  const configuredFormTier = options.draft.tierId !== null && Number.isSafeInteger(options.draft.tierId) &&
    options.draft.tierId >= 0 && !!capabilities?.configuredTierIds.includes(options.draft.tierId);
  const savedTierId = selectedSavedIntent && typeof record?.tierId === "number" ? record.tierId : null;
  const configuredSavedTier = savedTierId !== null && Number.isSafeInteger(savedTierId) && savedTierId >= 0 &&
    !!capabilities?.configuredTierIds.includes(savedTierId);
  const configuredQuoteTier = selectedSavedIntent ? configuredSavedTier : configuredFormTier;
  const reservation = isRecord(record?.reservation) ? record.reservation : null;
  const voucher = reservation && isRecord(reservation.voucher) ? reservation.voucher : null;
  const promptChunks = typeof voucher?.promptChunks === "number" ? voucher.promptChunks : 0;
  const progress = isRecord(record?.promptProgress) ? record.promptProgress : null;
  const receivedChunks = typeof progress?.received === "number" ? progress.received : 0;
  const enabled: Record<ActionName, boolean> = {
    requestQuote: !!binding && recoveryReady && !state.restoring && !busy && !pendingReleased && (pendingQuoteRetry || newLaunchReady) && configuredQuoteTier &&
      (selectedSavedIntent || (!!options.systemPrompt && !!options.draft.name.trim() && options.draft.additionalPrompt.length <= 64_000)) &&
      startableAttempt && !anyUnresolvedAction && (!serverOnlyUnresolved || pendingQuoteRetry) &&
      (retiredIntent || (noUncertainty && noEnteredAction)),
    reviewQuote: !!binding && !state.restoring && !busy && connected && !!record && ["quoted", "paid", "queued"].includes(String(record.phase)) && noUncertainty,
    approve: !!binding && recoveryReady && !state.restoring && !busy && ownedQuoteReady && !!hasMatchingReview && record?.phase === "quoted" &&
      noUncertainty && noEnteredAction && !anyUnresolvedAction && !serverOnlyUnresolved,
    purchase: !!binding && recoveryReady && !state.restoring && !busy && ownedQuoteReady && !!hasMatchingReview && record?.phase === "quoted" &&
      noUncertainty && noEnteredAction && !anyUnresolvedAction && !serverOnlyUnresolved,
    reconcile: !!binding && !state.restoring && !busy && connected && !!record,
    uploadNext: !!binding && !state.restoring && !busy && connected && record?.phase === "paid" &&
      noEnteredAction && noUncertainty && promptChunks >= receivedChunks,
    closeExpired: !!binding && !state.restoring && !busy && connected && record?.phase === "quoted",
    discardDraft: !!binding && !state.restoring && !busy && connected && record?.phase === "draft" && noUncertainty && noEnteredAction,
    prepareFunding: !!binding && !!budgetVaultReference && !state.restoring && !state.restoreFailed && !busy && connected && !!record &&
      record.phase === "queued" && !isRecord(record.budgetFunding),
    depositFunding: !!binding && !!budgetVaultReference && !state.restoring && !state.restoreFailed && !busy && connected && !!record &&
      recoveryReady && !anyUnresolvedAction && !serverOnlyUnresolved && record.phase === "queued" && isRecord(record.budgetFunding) && record.budgetFunding.status === "intent",
    reconcileFunding: !!binding && !!budgetVaultReference && !state.restoring && !state.restoreFailed && !busy && connected && !!record && isRecord(record.budgetFunding),
    readFundingStatus: !!binding && !!budgetVaultReference && !state.restoring && !busy && connected && !!record && isRecord(record.budgetFunding),
    refundFunding: !!binding && !!budgetVaultReference && !state.restoring && !state.restoreFailed && !busy && connected && !!record &&
      recoveryReady && !anyUnresolvedAction && !serverOnlyUnresolved && isRecord(record.budgetFunding) && record.budgetFunding.status === "committed" && state.fundingStatus?.refundAvailable === true,
    startAnother: !!binding && recoveryReady && !state.restoring && !busy && newLaunchReady && configuredFormTier && !!options.systemPrompt &&
      !!options.draft.name.trim() && options.draft.additionalPrompt.length <= 64_000 && !anyUnresolvedAction && !serverOnlyUnresolved,
    selectAttempt: !!binding && recoveryReady && !state.restoring && !busy,
    refreshRecovery: !!binding && !busy,
  };

  const updateFor = (target: Binding, mutate: (previous: UiState) => UiState) => {
    if (bindingRef.current !== target || target.disposed || identityKeyRef.current !== target.key) return;
    setState((previous) => previous.binding === target ? mutate(previous) : previous);
  };

  function updateActivityForClaim(claim: BusyClaim, mutate: (current: LaunchActivitySnapshot) => LaunchActivitySnapshot): void {
    if (!claim.activityId || busyRef.current !== claim || !isBindingCurrent(claim.binding)) return;
    const current = activityRef.current;
    if (!current || current.id !== claim.activityId || current.status !== "running") return;
    const next = mutate(current);
    if (next === current) return;
    activityRef.current = next;
    setActivity(next);
  }

  function hasVerifiedTransactionPhase(activityId: string): boolean {
    const current = activityRef.current;
    return current?.id === activityId && current.steps.some((step) => step.status === "confirmed");
  }

  async function callOperation<T>(target: Binding, call: (operation: WalletOperation) => Promise<T>, deadlineMs?: number): Promise<T> {
    const current = optionsRef.current.wallet;
    if (!current || !isBindingCurrent(target)) throw new Error("The selected wallet changed.");
    const activityClaim = busyRef.current?.binding === target ? busyRef.current : null;
    const parent = current.beginOperation();
    const controller = new AbortController();
    let finished = false;
    const abort = (signal: AbortSignal) => {
      if (!controller.signal.aborted) controller.abort(signal.reason);
    };
    const onParentAbort = () => abort(parent.signal);
    const onBindingAbort = () => abort(target.controller.signal);
    if (parent.signal.aborted) onParentAbort();
    else parent.signal.addEventListener("abort", onParentAbort, { once: true });
    if (target.controller.signal.aborted) onBindingAbort();
    else target.controller.signal.addEventListener("abort", onBindingAbort, { once: true });
    const operation: WalletOperation = {
      signal: controller.signal,
      isCurrent: () => !finished && !controller.signal.aborted && parent.isCurrent() && isBindingCurrent(target),
      reportProgress: (code) => {
        try {
          if (finished || controller.signal.aborted || !activityClaim || busyRef.current !== activityClaim ||
              !activityClaim.activityId || !parent.isCurrent() || !isBindingCurrent(target)) return;
          updateActivityForClaim(activityClaim, (previous) => advanceLaunchActivity(previous, code));
        } catch { /* Progress is advisory and may not affect the paid action. */ }
      },
      finish: () => {
        if (finished) return;
        finished = true;
        parent.signal.removeEventListener("abort", onParentAbort);
        target.controller.signal.removeEventListener("abort", onBindingAbort);
        parent.finish();
      },
    };
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let onOperationAbort: (() => void) | undefined;
    try {
      const result = call(operation);
      if (deadlineMs === undefined) return await result;
      const interrupted = new Promise<never>((_, reject) => {
        onOperationAbort = () => reject(new DOMException("The operation was aborted", "AbortError"));
        if (controller.signal.aborted) onOperationAbort();
        else controller.signal.addEventListener("abort", onOperationAbort, { once: true });
      });
      deadline = setTimeout(() => {
        if (!controller.signal.aborted) controller.abort(new DOMException("The operation timed out", "TimeoutError"));
      }, deadlineMs);
      return await Promise.race([result, interrupted]);
    } finally {
      if (deadline !== undefined) clearTimeout(deadline);
      if (onOperationAbort) controller.signal.removeEventListener("abort", onOperationAbort);
      operation.finish();
    }
  }

  async function callEngine<T>(target: Binding, call: (engine: Engine, operation: WalletOperation) => Promise<T>): Promise<T> {
    return callOperation(target, (operation) => call(target.engine, operation));
  }

  async function loadRecovery(target: Binding, showProgress: boolean, quoteAction = false,
    mappedPending?: { attemptId: string; reservationId: string }): Promise<boolean> {
    const epoch = ++target.recoveryEpoch;
    target.refundedTerminalProofs.clear();
    const currentWallet = optionsRef.current.wallet;
    const capturedSession: WalletSessionDescriptor | null = currentWallet?.session ?? null;
    if (!capturedSession || capturedSession.schemaVersion !== 2 || capturedSession.chainId !== base.id ||
        capturedSession.address.toLowerCase() !== target.account || capturedSession.expiresAtMs <= Date.now() ||
        capturedSession.idleExpiresAtMs <= Date.now() || !currentWallet?.client) {
      updateFor(target, (previous) => ({ ...previous, restoring: false, recoveryState: "unavailable", recoveryConflict: false,
        restoreFailed: true, error: GENERIC_ERROR }));
      return false;
    }
    if (showProgress) updateFor(target, (previous) => ({ ...previous, restoring: true, recoveryState: "checking", error: null }));
    let localRows: readonly RecordValue[] = [];
    let selectedId: string | null = null;
    let selectedRecord: RecordValue | null = null;
    try {
      const [localValue, selectedValue] = await Promise.all([
        target.store.listForPayer(target.account), target.store.loadSelected(target.account),
      ]);
      if (!isBindingCurrent(target) || target.recoveryEpoch !== epoch || optionsRef.current.wallet?.session !== capturedSession ||
          capturedSession.expiresAtMs <= Date.now() || capturedSession.idleExpiresAtMs <= Date.now()) return false;
      if (!Array.isArray(localValue) || localValue.length > 100 || localValue.some((item) => !isRecord(item) ||
          typeof item.attemptId !== "string" || typeof item.payer !== "string" || item.payer.toLowerCase() !== target.account ||
          !isRecord(item.deployment))) throw new Error(GENERIC_ERROR);
      localRows = localValue;
      if (selectedValue !== null) {
        if (!isRecord(selectedValue) || typeof selectedValue.attemptId !== "string" ||
            !localRows.some((item) => item.attemptId === selectedValue.attemptId)) throw new Error(GENERIC_ERROR);
        selectedId = selectedValue.attemptId;
        if (sameDeployment(selectedValue.deployment as DeploymentReference, target.deployment)) {
          const fresh = await callEngine(target, (engine, operation) => engine.read(selectedId!, operation));
          if (!isBindingCurrent(target) || target.recoveryEpoch !== epoch || !isRecord(fresh) || fresh.attemptId !== selectedId ||
              typeof fresh.payer !== "string" || fresh.payer.toLowerCase() !== target.account ||
              !sameDeployment(fresh.deployment as DeploymentReference, target.deployment)) throw new Error(GENERIC_ERROR);
          selectedRecord = fresh;
        }
      }
      const remoteValue = await callOperation(target, (operation) => currentWallet.client.listWalletReservations(capturedSession, operation));
      if (!isBindingCurrent(target) || target.recoveryEpoch !== epoch || optionsRef.current.wallet?.session !== capturedSession ||
          capturedSession.expiresAtMs <= Date.now() || capturedSession.idleExpiresAtMs <= Date.now()) return false;
      if (!validRecoverySnapshot(remoteValue, target.account)) throw new Error(GENERIC_ERROR);
      const remote = remoteValue.records;
      for (const row of remote) {
        const matches = sameDeployment(row.deployment, target.deployment);
        if ((row.scope === "configured_deployment") !== matches) throw new Error(GENERIC_ERROR);
      }
      const terminalProofs = new Set<string>();
      const paidServerOnly = remote.filter((row) => row.status === "paid" && row.scope === "configured_deployment" &&
        !localRows.some((local) => matchesReservation(local, row))).slice(0, 10);
      if (paidServerOnly.length > 0) await Promise.all(paidServerOnly.map(async (row) => {
        try {
          const verified = await callOperation(target, async (operation) => {
            if (row.payer.toLowerCase() !== target.account || !sameDeployment(row.deployment, target.deployment) ||
                !operation.isCurrent()) return false;
            const candidate = await findPurchaseCandidate(target.ports.lookup, row.reservationId as `0x${string}`, operation.signal);
            if (!candidate || !operation.isCurrent()) return false;
            const terminal = await verifyRefundedTerminalPurchase({ deployment: target.deployment, payer: target.account,
              reservationId: row.reservationId as `0x${string}`, agentId: candidate.agentId }, target.ports.rpc, operation.signal);
            return terminal && operation.isCurrent();
          }, 8_000);
          if (verified && isBindingCurrent(target) && target.recoveryEpoch === epoch &&
              optionsRef.current.wallet?.session === capturedSession && capturedSession.expiresAtMs > Date.now() &&
              capturedSession.idleExpiresAtMs > Date.now()) {
            terminalProofs.add(refundedTerminalProofKey(target, row, epoch, capturedSession));
          }
        } catch { /* A failed, stale, or unavailable proof leaves this reservation unresolved. */ }
      }));
      if (!isBindingCurrent(target) || target.recoveryEpoch !== epoch || optionsRef.current.wallet?.session !== capturedSession ||
          capturedSession.expiresAtMs <= Date.now() || capturedSession.idleExpiresAtMs <= Date.now()) return false;
      const recoveryConflict = localRows.some((item) => {
        return !!reservationId(item) && !remote.some((row) => matchesReservation(item, row)) && !isTerminalLocalRecord(item);
      });
      const unresolved = localRows.some(hasUnresolvedDispatch);
      const mappedRow = mappedPending && localRows.some((local) => local.attemptId === mappedPending.attemptId &&
        local.phase === "reservation_pending" && (reservationId(local) === null || reservationId(local) === mappedPending.reservationId))
        ? remote.find((row) => row.reservationId === mappedPending.reservationId && row.scope === "configured_deployment" &&
          (row.status === "signing" || row.status === "quoted") && sameDeployment(row.deployment, target.deployment))
        : undefined;
      if (mappedPending && !mappedRow) return false;
      for (const key of terminalProofs) target.refundedTerminalProofs.add(key);
      target.selectedAttemptId = selectedId;
      updateFor(target, (previous) => ({ ...previous, record: selectedRecord, selectedAttemptId: selectedId,
        review: selectedRecord && previous.review && consentKey(previous.review.consent) === consentKey(selectedRecord.consent)
          ? previous.review : null,
        acknowledgedKey: selectedRecord && previous.review && consentKey(previous.review.consent) === consentKey(selectedRecord.consent) &&
          previous.acknowledgedKey === consentKey(selectedRecord.consent) ? previous.acknowledgedKey : null,
        uncertainAttemptId: selectedRecord && hasUnresolvedDispatch(selectedRecord)
          ? String(selectedRecord.attemptId) : null, localAttempts: localRows.map((item) => item), remoteReservations: remote,
        recoveryState: recoveryConflict ? "unavailable" : "ready", recoveryConflict, restoring: false,
        restoreFailed: recoveryConflict, error: recoveryConflict ? GENERIC_ERROR : null, fundingStatus: null }));
      if (!recoveryConflict && selectedRecord && isRecord(selectedRecord.budgetFunding)) void loadFundingStatus(target, String(selectedRecord.attemptId));
      const serverOnlyPending = remote.some((row) => ["signing", "quoted", "paid", "reconciling"].includes(row.status) &&
        !localRows.some((local) => matchesReservation(local, row)) && row !== mappedRow &&
        !(row.status === "paid" && terminalProofs.has(refundedTerminalProofKey(target, row, epoch, capturedSession))));
      return !recoveryConflict && !unresolved && !serverOnlyPending;
    } catch (error) {
      if (target.recoveryEpoch === epoch) target.refundedTerminalProofs.clear();
      if (quoteAction) quoteDiagnostic("recovery_error", { code: quoteErrorCode(error) });
      if (isBindingCurrent(target) && target.recoveryEpoch === epoch) updateFor(target, (previous) => ({ ...previous,
        record: selectedRecord, selectedAttemptId: selectedId, localAttempts: localRows.map((item) => item),
        recoveryState: "unavailable", recoveryConflict: true,
        restoring: false, restoreFailed: true, error: GENERIC_ERROR }));
      return false;
    }
  }

  async function loadFundingStatus(target: Binding, attemptId: string): Promise<boolean> {
    const recoveryEpoch = target.recoveryEpoch;
    try {
      const status = await callOperation(target, (operation) => target.funding.readFundingStatus(attemptId, operation));
      const stored = await target.store.load(attemptId);
      if (!isBindingCurrent(target) || target.recoveryEpoch !== recoveryEpoch || target.selectedAttemptId !== attemptId || !isRecord(status)) return false;
      updateFor(target, (previous) => ({ ...previous, record: stored ?? previous.record, fundingStatus: status, error: null }));
      return true;
    } catch {
      if (isBindingCurrent(target) && target.recoveryEpoch === recoveryEpoch && target.selectedAttemptId === attemptId) {
        updateFor(target, (previous) => ({ ...previous, fundingStatus: null, error: GENERIC_ERROR }));
      }
      return false;
    }
  }

  function isBindingCurrent(target: Binding): boolean {
    return bindingRef.current === target && !target.disposed && identityKeyRef.current === target.key;
  }

  function isNewLaunchReadyNow(target: Binding, tierId: number): boolean {
    const latest = optionsRef.current;
    const liveWallet = latest.wallet;
    const liveSession = liveWallet?.session;
    const liveCapabilities = liveWallet?.capabilities;
    return isBindingCurrent(target) && !!liveSession && liveSession.schemaVersion === 2 && liveSession.chainId === base.id &&
      liveSession.address.toLowerCase() === target.account && liveSession.expiresAtMs > Date.now() && liveSession.idleExpiresAtMs > Date.now() &&
      sameDeployment(latest.deployment, target.deployment) && sameDeployment(liveCapabilities?.deployment, target.deployment) &&
      liveCapabilities?.walletAuthentication === true &&
      liveCapabilities.admission === true && liveCapabilities.payments === true && liveCapabilities.configuredTierIds.includes(tierId);
  }

  function isOwnedQuoteReadyNow(target: Binding): boolean {
    const latest = optionsRef.current;
    const liveWallet = latest.wallet;
    const liveSession = liveWallet?.session;
    const liveCapabilities = liveWallet?.capabilities;
    return isBindingCurrent(target) && !!liveSession && liveSession.schemaVersion === 2 && liveSession.chainId === base.id &&
      liveSession.address.toLowerCase() === target.account && liveSession.expiresAtMs > Date.now() && liveSession.idleExpiresAtMs > Date.now() &&
      sameDeployment(latest.deployment, target.deployment) && sameDeployment(liveCapabilities?.deployment, target.deployment) &&
      liveCapabilities?.walletAuthentication === true;
  }

  async function refresh(target: Binding, attemptId: string, latchOnFailure = false): Promise<RecordValue | null> {
    const recoveryEpoch = target.recoveryEpoch;
    try {
      const fresh = await callEngine(target, (engine, operation) => engine.read(attemptId, operation));
      if (!isBindingCurrent(target) || target.selectedAttemptId !== attemptId || target.recoveryEpoch !== recoveryEpoch) return null;
      if (!isRecord(fresh)) throw new Error(GENERIC_ERROR);
      updateFor(target, (previous) => ({ ...previous, record: fresh,
        localAttempts: previous.localAttempts.map((item) => item.attemptId === fresh.attemptId ? fresh : item),
        review: previous.review && consentKey(previous.review.consent) === consentKey(fresh.consent) ? previous.review : null,
        acknowledgedKey: previous.acknowledgedKey === consentKey(fresh.consent) ? previous.acknowledgedKey : null,
        uncertainAttemptId: hasUnresolvedDispatch(fresh) ? attemptId : null, error: null }));
      return fresh;
    } catch {
      if (isBindingCurrent(target) && target.selectedAttemptId === attemptId && target.recoveryEpoch === recoveryEpoch) updateFor(target, (previous) => ({ ...previous,
        uncertainAttemptId: latchOnFailure ? attemptId : previous.uncertainAttemptId, error: latchOnFailure ? UNCERTAIN_ERROR : GENERIC_ERROR }));
      return null;
    }
  }

  async function runAction(name: ActionName, action: (target: Binding, current: RecordValue | null, progress: ActionProgress) => Promise<void>): Promise<void> {
    const target = binding;
    if (!target) {
      if (name === "requestQuote") quoteDiagnostic("action_blocked", { reason: "no_binding" });
      return;
    }
    if (!enabled[name]) {
      if (name === "requestQuote") quoteDiagnostic("action_blocked", { reason: "disabled" });
      return;
    }
    if (busyRef.current?.binding === target) {
      if (name === "requestQuote") quoteDiagnostic("action_blocked", { reason: "busy" });
      return;
    }
    const token = {};
    activityCounterRef.current += 1;
    const activityId = `${activityCounterRef.current}:${Date.now()}`;
    const claim: BusyClaim = { binding: target, token, action: name, activityId };
    busyRef.current = claim;
    const initialActivity = createLaunchActivity(name, activityId);
    activityRef.current = initialActivity;
    setActivity(initialActivity);
    const progress: ActionProgress = {
      stage: (code) => updateActivityForClaim(claim, (previous) => advanceLaunchActivity(previous, code)),
      finish: (status, message) => updateActivityForClaim(claim, (previous) => {
        const finished = finishLaunchActivity(previous, status, message ?? null);
        return finished.status === "running" || finished.status === "submitted" ? finished : setLaunchActivityVisible(finished, true);
      }),
    };
    updateFor(target, (previous) => ({ ...previous, busy: claim, error: null,
      ...(name === "reviewQuote" ? { review: null, acknowledgedKey: null } : {}) }));
    if (name === "requestQuote") quoteDiagnostic("action_start");
    try {
      await action(target, record, progress);
      if (activityRef.current?.id === activityId && activityRef.current.status === "running") {
        progress.finish("needs_check", "No verified update was returned. Check the saved status before trying again.");
      }
    }
    catch (error) {
      if (name === "requestQuote") quoteDiagnostic("action_error", { code: quoteErrorCode(error) });
      if (isBindingCurrent(target)) updateFor(target, (previous) => ({ ...previous, error: name === "reviewQuote" ? reviewErrorText(error) : GENERIC_ERROR }));
      if (activityRef.current?.id === activityId && activityRef.current.status === "running") {
        const submitted = activityRef.current.steps.some((step) => step.status === "submitted");
        progress.finish(submitted ? "needs_check" : "failed", submitted
          ? "A transaction was submitted, but its result is not confirmed. Check transaction status before trying again."
          : "This action could not be completed. Check the saved status before trying again.");
      }
    } finally {
      if (busyRef.current === claim) {
        busyRef.current = null;
        updateFor(target, (previous) => ({ ...previous, busy: null }));
      }
    }
  }

  function setActivityVisibility(visible: boolean): void {
    const current = activityRef.current;
    if (!current || !binding || !isBindingCurrent(binding)) return;
    const next = setLaunchActivityVisible(current, visible);
    activityRef.current = next;
    setActivity(next);
  }

  async function selectAttempt(attemptId: string): Promise<void> {
    await runAction("selectAttempt", async (target, _current, operationProgress) => {
    const candidate = state.localAttempts.find((item) => item.attemptId === attemptId && item.payer?.toString().toLowerCase() === target.account);
    if (!candidate) {
      const remote = state.remoteReservations.find((item) => item.reservationId === attemptId);
      if (!remote) return;
      target.recoveryEpoch++;
      target.selectedAttemptId = attemptId;
      updateFor(target, (previous) => ({ ...previous, record: null, selectedAttemptId: attemptId, review: null,
        acknowledgedKey: null, uncertainAttemptId: null, fundingStatus: null, error: null }));
      operationProgress.finish("completed", "The service record is selected for recovery-only review.");
      return;
    }
    target.recoveryEpoch++;
    const stored = await target.store.selectAttempt(attemptId);
    if (!isBindingCurrent(target) || !isRecord(stored) || stored.attemptId !== attemptId ||
        stored.payer?.toString().toLowerCase() !== target.account) throw new Error(GENERIC_ERROR);
    let fresh: RecordValue | null = null;
    if (sameDeployment(stored.deployment as DeploymentReference, target.deployment)) {
      operationProgress.stage("checking_chain_state");
      const verified = await callEngine(target, (engine, operation) => engine.read(attemptId, operation));
      if (!isBindingCurrent(target) || !isRecord(verified) || verified.attemptId !== attemptId ||
          verified.payer?.toString().toLowerCase() !== target.account ||
          !sameDeployment(verified.deployment as DeploymentReference, target.deployment)) throw new Error(GENERIC_ERROR);
      fresh = verified;
    }
    updateFor(target, (previous) => ({ ...previous, record: fresh, selectedAttemptId: attemptId, review: null,
      acknowledgedKey: null, uncertainAttemptId: fresh && hasUnresolvedDispatch(fresh) ? attemptId : null,
      fundingStatus: null, error: null }));
    target.selectedAttemptId = attemptId;
    if (fresh && isRecord(fresh.budgetFunding)) void loadFundingStatus(target, attemptId);
    operationProgress.finish("completed", "The saved launch is selected and its current status was checked.");
    });
  }

  const refreshRecovery = () => runAction("refreshRecovery", async (target, _current, operationProgress) => {
    operationProgress.stage("checking_recovery");
    if (!await loadRecovery(target, true)) return;
    operationProgress.stage("reading_saved_launch");
    operationProgress.finish("completed", "Saved launch and payment records were checked.");
  });

  const startAnother = () => runAction("startAnother", async (target, _current, operationProgress) => {
    operationProgress.stage("checking_recovery");
    if (!await loadRecovery(target, true)) return;
    const latest = optionsRef.current;
    const tierId = latest.draft.tierId;
    if (tierId === null || !Number.isSafeInteger(tierId) || !latest.systemPrompt || !isNewLaunchReadyNow(target, tierId) ||
        anyUnresolvedAction || serverOnlyUnresolved) return;
    operationProgress.stage("checking_launch_eligibility");
    const input = { deployment: target.deployment, payer: target.account, tierId,
      name: latest.draft.name, additionalPrompt: latest.draft.additionalPrompt, systemPrompt: latest.systemPrompt };
    const created: unknown = await callEngine(target, (engine, operation) =>
      (engine as IntentEngine).createDraft(input, operation, { intent: "new" }));
    if (!isBindingCurrent(target) || !isRecord(created) || created.kind !== "created" || typeof created.attemptId !== "string") return;
    const attemptId = created.attemptId;
    const selected = await target.store.selectAttempt(attemptId);
    if (!isBindingCurrent(target) || !isRecord(selected) || selected.attemptId !== attemptId ||
        selected.payer?.toString().toLowerCase() !== target.account) throw new Error(GENERIC_ERROR);
    const fresh = await callEngine(target, (engine, operation) => engine.read(attemptId, operation));
    operationProgress.stage("reading_saved_launch");
    const localAttempts = await target.store.listForPayer(target.account);
    if (!isBindingCurrent(target) || !isRecord(fresh) || fresh.attemptId !== attemptId ||
        !sameDeployment(fresh.deployment as DeploymentReference, target.deployment) ||
        !localAttempts.some((item) => item.attemptId === attemptId)) throw new Error(GENERIC_ERROR);
    target.selectedAttemptId = attemptId;
    updateFor(target, (previous) => ({ ...previous, record: fresh, selectedAttemptId: attemptId,
      localAttempts: [...localAttempts], review: null, acknowledgedKey: null, uncertainAttemptId: null, error: null }));
    operationProgress.finish("completed", "The new launch draft is saved.");
  });

  async function resolvePendingQuote(target: Binding, expectedAttemptId: string): Promise<
    { kind: "mapped"; attemptId: string; reservationId: string; state: "signing" | "quoted" | "released" } |
    { kind: "absent"; attemptId: string }
  > {
    const session = optionsRef.current.wallet?.session;
    if (!session || session.schemaVersion !== 2 || session.chainId !== 8453 || session.address.toLowerCase() !== target.account ||
        session.expiresAtMs <= Date.now() || session.idleExpiresAtMs <= Date.now()) throw new Error(GENERIC_ERROR);
    const selected = await callOperation(target, () => target.store.loadSelected(target.account));
    if (!isBindingCurrent(target) || !isRecord(selected) || selected.attemptId !== expectedAttemptId ||
        selected.phase !== "reservation_pending" || selected.payer?.toString().toLowerCase() !== target.account ||
        !sameDeployment(selected.deployment as DeploymentReference, target.deployment) || !isRecord(selected.draft) ||
        !isRecord(selected.draft.systemPrompt) || typeof selected.draft.additionalPrompt !== "string" ||
        typeof selected.draft.systemPrompt.systemPromptHash !== "string" || typeof selected.idempotencyKey !== "string") {
      throw new Error(GENERIC_ERROR);
    }
    const fresh = await callEngine(target, (engine, operation) => engine.read(expectedAttemptId, operation));
    if (!isBindingCurrent(target) || !isRecord(fresh) || fresh.attemptId !== expectedAttemptId || fresh.phase !== "reservation_pending" ||
        typeof fresh.payer !== "string" || fresh.payer.toLowerCase() !== target.account ||
        fresh.idempotencyKey !== selected.idempotencyKey || !sameDeployment(fresh.deployment as DeploymentReference, target.deployment) ||
        fresh.pendingReservationId !== selected.pendingReservationId ||
        !isRecord(fresh.draft) || !isRecord(fresh.draft.systemPrompt) || !sameData(fresh.draft, selected.draft)) throw new Error(GENERIC_ERROR);
    const client = optionsRef.current.wallet?.client;
    if (!client) throw new Error(GENERIC_ERROR);
    const attempt = client.reservationAttempt({ session, idempotencyKey: String(fresh.idempotencyKey), tierId: Number(fresh.tierId),
      name: String(fresh.name), additionalPrompt: String(fresh.draft.additionalPrompt),
      systemPromptHash: fresh.draft.systemPrompt.systemPromptHash as `0x${string}` });
    let mapping: unknown;
    try { mapping = await callOperation(target, (operation) => attempt.resolve(operation)); }
    catch (error) {
      if (error instanceof WalletReservationError && error.code === "not_found" && selected.pendingReservationId == null && selected.reservation === null) {
        return { kind: "absent", attemptId: expectedAttemptId };
      }
      throw error;
    }
    if (!isBindingCurrent(target) || !isRecord(mapping) || mapping.schemaVersion !== 2 ||
        (mapping.state !== "signing" && mapping.state !== "quoted" && mapping.state !== "released") || typeof mapping.reservationId !== "string" ||
        !/^0x[0-9a-f]{64}$/.test(mapping.reservationId) || !sameDeployment(mapping.deployment as DeploymentReference, target.deployment) ||
        (selected.pendingReservationId != null && mapping.reservationId !== selected.pendingReservationId)) throw new Error(GENERIC_ERROR);
    return { kind: "mapped", attemptId: expectedAttemptId, reservationId: mapping.reservationId, state: mapping.state };
  }

  const requestQuote = () => runAction("requestQuote", async (target, capturedRecord, operationProgress) => {
    operationProgress.stage("checking_recovery");
    quoteDiagnostic("recovery_start");
    let mappedPending: { attemptId: string; reservationId: string } | undefined;
    let pendingReservationAbsent = false;
    if (capturedRecord?.phase === "reservation_pending" && typeof capturedRecord.attemptId === "string") {
      try {
        const resolution = await resolvePendingQuote(target, capturedRecord.attemptId);
        if (resolution.kind === "absent") pendingReservationAbsent = true;
        else if (resolution.state === "signing" || resolution.state === "quoted") {
          mappedPending = { attemptId: resolution.attemptId, reservationId: resolution.reservationId };
        }
      }
      catch (error) {
        quoteDiagnostic("recovery_error", { code: quoteErrorCode(error) });
        quoteDiagnostic("action_blocked", { reason: "recovery_unavailable" });
        return;
      }
    }
    if (!await loadRecovery(target, true, true, mappedPending)) {
      quoteDiagnostic("action_blocked", { reason: "recovery_unavailable" });
      return;
    }
    operationProgress.stage("reading_saved_launch");
    quoteDiagnostic("recovery_ready");
    let currentRecord = capturedRecord;
    quoteDiagnostic("selected_read_start");
    let selectedValue: RecordValue | null;
    try { selectedValue = await target.store.loadSelected(target.account); }
    catch (error) {
      quoteDiagnostic("selected_read_error", { code: quoteErrorCode(error) });
      throw error;
    }
    if (selectedValue) {
      if (typeof selectedValue.attemptId !== "string" || !sameDeployment(selectedValue.deployment as DeploymentReference, target.deployment)) {
        quoteDiagnostic("action_blocked", { reason: "selection_invalid" });
        return;
      }
      let validated: unknown;
      try { validated = await callEngine(target, (engine, operation) => engine.read(selectedValue!.attemptId as string, operation)); }
      catch (error) {
        quoteDiagnostic("selected_read_error", { code: quoteErrorCode(error) });
        throw error;
      }
      if (!isBindingCurrent(target) || !isRecord(validated) || validated.attemptId !== selectedValue.attemptId ||
          !sameDeployment(validated.deployment as DeploymentReference, target.deployment)) {
        quoteDiagnostic("action_blocked", { reason: "selection_invalid" });
        return;
      }
      currentRecord = validated;
    } else currentRecord = null;
    quoteDiagnostic("selected_read_ok");
    const currentOptions = optionsRef.current;
    const liveWallet = currentOptions.wallet;
    const capturedSession: WalletSessionDescriptor | null = liveWallet?.session ?? null;
    const systemPrompt = currentOptions.systemPrompt;
    const isSavedIntent = !!currentRecord && (currentRecord.phase === "draft" || currentRecord.phase === "reservation_pending");
    const tierId = isSavedIntent
      ? typeof currentRecord?.tierId === "number" ? currentRecord.tierId : null
      : currentOptions.draft.tierId;
    const quoteReadyNow = currentRecord?.phase === "reservation_pending" ?
      (pendingReservationAbsent ? tierId !== null && isNewLaunchReadyNow(target, tierId) : isOwnedQuoteReadyNow(target)) :
      tierId !== null && isNewLaunchReadyNow(target, tierId);
    if (!capturedSession || (!isSavedIntent && !systemPrompt) || tierId === null || !Number.isSafeInteger(tierId) || !quoteReadyNow) {
      quoteDiagnostic("action_blocked", { reason: "readiness" });
      return;
    }
    const retiredRecord = currentRecord?.phase === "retired_unused" || currentRecord?.phase === "retired_unsent" ||
      (currentRecord?.phase === "terminal_onchain" && hasVerifiedTerminalOnchainHistory(currentRecord));
    if (currentRecord && currentRecord.phase !== "draft" && currentRecord.phase !== "reservation_pending" && !retiredRecord) {
      quoteDiagnostic("action_blocked", { reason: "record_not_quoteable" });
      return;
    }
    let attemptId = !retiredRecord && typeof currentRecord?.attemptId === "string" ? currentRecord.attemptId : null;
    if (!attemptId) {
      if (!systemPrompt) {
        quoteDiagnostic("action_blocked", { reason: "readiness" });
        return;
      }
      const input = { deployment: target.deployment, payer: target.account, tierId,
        name: currentOptions.draft.name, additionalPrompt: currentOptions.draft.additionalPrompt, systemPrompt };
      const created: unknown = await callEngine(target, (engine, operation) => engine.createDraft(input, operation));
      if (!isBindingCurrent(target) || !isRecord(created) || typeof created.attemptId !== "string") {
        quoteDiagnostic("action_blocked", { reason: "readiness_changed" });
        return;
      }
      attemptId = created.attemptId;
      const selected = await target.store.selectAttempt(attemptId);
      if (!isBindingCurrent(target) || !isRecord(selected) || selected.attemptId !== attemptId ||
          typeof selected.payer !== "string" || selected.payer.toLowerCase() !== target.account ||
          !sameDeployment(selected.deployment as DeploymentReference, target.deployment)) throw new Error(GENERIC_ERROR);
      target.selectedAttemptId = attemptId;
      updateFor(target, (previous) => ({ ...previous, selectedAttemptId: attemptId,
        localAttempts: previous.localAttempts.some((item) => item.attemptId === attemptId)
          ? previous.localAttempts : [...previous.localAttempts, selected], error: null }));
      const draft = await refresh(target, created.attemptId);
      if (!draft || !["draft", "reservation_pending"].includes(String(draft.phase))) {
        quoteDiagnostic("action_blocked", { reason: "selection_invalid" });
        return;
      }
    }
    const quoteAttemptId = attemptId;
    const quoteStillReady = currentRecord?.phase === "reservation_pending" ?
      (pendingReservationAbsent ? isNewLaunchReadyNow(target, tierId) : isOwnedQuoteReadyNow(target)) : isNewLaunchReadyNow(target, tierId);
    if (!quoteAttemptId || !isBindingCurrent(target) || optionsRef.current.wallet?.session !== capturedSession || !quoteStillReady) {
      quoteDiagnostic("action_blocked", { reason: "readiness_changed" });
      return;
    }
    try {
      quoteDiagnostic("submit_start");
      await callEngine(target, (engine, operation) => engine.requestQuote(quoteAttemptId, capturedSession, operation));
      quoteDiagnostic("submit_ok");
    } catch (error) {
      quoteDiagnostic("submit_error", { code: quoteErrorCode(error) });
      const afterFailure = isBindingCurrent(target) ? await refresh(target, quoteAttemptId) : null;
      if (afterFailure?.phase === "quoted" && isRecord(afterFailure.reservation)) {
        operationProgress.stage("saving_offer");
        operationProgress.finish("completed", "The signed offer was saved and is ready to review.");
        return;
      }
      operationProgress.finish("needs_check", "The offer request may still be processing. Check saved status before requesting another quote.");
      throw new Error(GENERIC_ERROR);
    }
    if (isBindingCurrent(target)) {
      const saved = await refresh(target, quoteAttemptId);
      if (saved && saved.phase === "quoted" && isRecord(saved.reservation)) {
        operationProgress.stage("saving_offer");
        operationProgress.finish("completed", "The signed offer is saved and ready to review.");
      }
    }
  });

  const reviewQuote = () => runAction("reviewQuote", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    let result: unknown;
    try { result = await callEngine(target, (engine, operation) => engine.reviewQuote(current.attemptId as string, operation)); }
    catch (error) {
      if (isBindingCurrent(target)) await refresh(target, current.attemptId);
      throw error;
    }
    const fresh = await refresh(target, current.attemptId as string);
    if (!isBindingCurrent(target) || !isRecord(result) || !isRecord(result.consent) || !fresh) return;
    const nextReview = { observation: result.observation, consent: result.consent as unknown as V2PaidLaunchConsent };
    if (nextReview.consent.attemptId !== fresh.attemptId || nextReview.consent.draftDigest !== fresh.draftDigest ||
        nextReview.consent.quoteDigest !== (fresh.consent as RecordValue | null)?.quoteDigest) return;
    updateFor(target, (previous) => ({ ...previous, review: nextReview, acknowledgedKey: null }));
    operationProgress.stage("saving_review");
    operationProgress.finish("completed", "The offer review is saved. Review it before approving or paying.");
  });

  const financialAction = (name: "approve" | "purchase") => () => runAction(name, async (target, current, operationProgress) => {
    operationProgress.stage("checking_recovery");
    if (!await loadRecovery(target, true)) return;
    if (!current || !state.review || !acknowledged || typeof current.attemptId !== "string") return;
    const capturedSession = optionsRef.current.wallet?.session;
    if (!capturedSession || capturedSession.schemaVersion !== 2 || capturedSession.chainId !== 8453 ||
        capturedSession.address.toLowerCase() !== target.account || capturedSession.expiresAtMs <= Date.now() ||
        capturedSession.idleExpiresAtMs <= Date.now()) return;
    const capturedConsent = state.review.consent;
    const selected = await target.store.loadSelected(target.account);
    if (!isBindingCurrent(target) || !selected || selected.attemptId !== current.attemptId) return;
    const fresh = await callEngine(target, (engine, operation) => engine.read(current.attemptId as string, operation));
    if (!isBindingCurrent(target) || !isRecord(fresh) || fresh.attemptId !== current.attemptId ||
        capturedConsent.attemptId !== fresh.attemptId || capturedConsent.draftDigest !== fresh.draftDigest ||
        capturedConsent.quoteDigest !== (isRecord(fresh.consent) ? fresh.consent.quoteDigest : null) ||
        optionsRef.current.wallet?.session !== capturedSession) return;
    const consent = capturedConsent;
    try {
      const result = await callEngine(target, (engine, operation) => engine[name](consent, operation, capturedSession));
      const resultRecord = result as unknown as RecordValue;
      if (isRecord(resultRecord) && resultRecord.kind === "submitted" && typeof resultRecord.transactionHash === "string") {
        operationProgress.stage(name === "approve" ? "transaction_submitted_approval" : "transaction_submitted_purchase");
        operationProgress.finish("submitted", "The transaction was submitted. Confirmation is still pending.");
      } else if (name === "approve" && isRecord(result) && result.kind === "already_sufficient") {
        operationProgress.finish("completed", "The saved quote already has sufficient USDC allowance. No approval transaction was requested.");
      }
    } catch (error) {
      quoteDiagnostic("financial_action_error", { code: quoteErrorCode(error) });
      const attemptId = String(current.attemptId);
      const recoveryEpoch = target.recoveryEpoch;
      if (!isBindingCurrent(target)) return;
      await refresh(target, attemptId, true);
      if (!isBindingCurrent(target) || target.selectedAttemptId !== attemptId || target.recoveryEpoch !== recoveryEpoch) return;
      updateFor(target, (previous) => ({ ...previous,
        error: previous.uncertainAttemptId === attemptId ? UNCERTAIN_ERROR : GENERIC_ERROR }));
      return;
    }
    if (isBindingCurrent(target)) await refresh(target, String(current.attemptId), true);
  });

  const reconcile = () => runAction("reconcile", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const reconciled = await callEngine(target, (engine, operation) => engine.reconcile(current.attemptId as string, operation));
      if (isRecord(reconciled) && !hasUnresolvedDispatch(reconciled)) {
        const confirmed = activityRef.current?.id && hasVerifiedTransactionPhase(activityRef.current.id);
        operationProgress.finish(confirmed ? "confirmed" : "completed", confirmed
          ? "A transaction was confirmed on chain and the saved status was updated."
          : "Saved launch status was reconciled.");
      }
    }
    catch { if (isBindingCurrent(target)) { await refresh(target, current.attemptId as string, true); return; } }
    if (isBindingCurrent(target)) {
      const fresh = await refresh(target, current.attemptId as string);
      if (fresh && !hasUnresolvedDispatch(fresh)) {
        const currentActivityId = activityRef.current?.id;
        const confirmed = !!currentActivityId && hasVerifiedTransactionPhase(currentActivityId);
        operationProgress.finish(confirmed ? "confirmed" : "completed", confirmed
          ? "A transaction was confirmed on chain and the saved status was updated."
          : "Saved launch status was reconciled.");
      }
    }
  });

  const uploadNext = () => runAction("uploadNext", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const uploaded = await callEngine(target, (engine, operation) => engine.uploadNext(current.attemptId as string, operation));
      const uploadedRecord = uploaded as unknown as RecordValue;
      if (isRecord(uploadedRecord) && typeof uploadedRecord.lastTransactionHash === "string") {
        operationProgress.stage("prompt_progress_verified");
        operationProgress.finish("confirmed", "The prompt upload was verified on chain.");
      } else if (isRecord(uploadedRecord) && uploadedRecord.phase === "queued") {
        operationProgress.finish("completed", "The prompt is already fully present on chain.");
      }
    }
    catch { if (isBindingCurrent(target)) { await refresh(target, current.attemptId as string, true); return; } }
    if (isBindingCurrent(target)) await refresh(target, current.attemptId as string, true);
  });

  const closeExpired = () => runAction("closeExpired", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const closed = await callEngine(target, (engine, operation) => engine.closeExpired(current.attemptId as string, operation));
      if (isRecord(closed) && closed.phase === "retired_unused" && isRecord(closed.finalizedEvidence)) {
        operationProgress.stage("closing_offer");
        operationProgress.finish("completed", "The expired offer was closed using finalized chain evidence.");
      }
    }
    catch { if (isBindingCurrent(target)) { await refresh(target, current.attemptId as string, true); return; } }
    if (isBindingCurrent(target)) await refresh(target, current.attemptId as string);
  });

  const discardDraft = () => runAction("discardDraft", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const discarded = await callEngine(target, (engine, operation) => engine.discardDraft(current.attemptId as string, operation));
      if (isRecord(discarded) && discarded.phase === "retired_unsent" && discarded.activeAction == null) {
        operationProgress.stage("discarding_draft");
        operationProgress.finish("completed", "The unsent draft was discarded.");
      }
    }
    catch { if (isBindingCurrent(target)) { await refresh(target, current.attemptId as string, true); return; } }
    if (isBindingCurrent(target)) await refresh(target, current.attemptId as string);
  });

  const prepareFunding = (amount: string) => runAction("prepareFunding", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    operationProgress.stage("checking_launch_eligibility");
    const saved = await callOperation(target, (operation) => target.funding.prepareFunding(current.attemptId as string, amount, operation));
    if (isBindingCurrent(target)) await refresh(target, current.attemptId);
    if (isRecord(saved) && saved.status === "intent" && saved.activeAction == null) {
      operationProgress.stage("saving_budget_choice");
      operationProgress.finish("completed", "The exact starting budget choice was saved. No wallet transaction was sent.");
    }
  });

  const depositFunding = () => runAction("depositFunding", async (target, current, operationProgress) => {
    if (!await loadRecovery(target, true)) return;
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const deposited = await callOperation(target, (operation) => target.funding.depositFunding(current.attemptId as string, operation));
      if (isRecord(deposited) && deposited.status === "committed" && isRecord(deposited.depositEvidence)) {
        operationProgress.finish("confirmed", "The budget deposit was verified on chain.");
      } else if (isRecord(deposited) && deposited.status === "intent" && isRecord(deposited.approvalEvidence)) {
        operationProgress.finish("confirmed", "Budget approval was verified on chain. The deposit has not been sent.");
      }
    }
    catch { if (isBindingCurrent(target)) { await refresh(target, current.attemptId, true); await loadFundingStatus(target, current.attemptId); return; } }
    if (isBindingCurrent(target)) { await refresh(target, current.attemptId, true); await loadFundingStatus(target, current.attemptId); }
  });

  const reconcileFunding = () => runAction("reconcileFunding", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const reconciled = await callOperation(target, (operation) => target.funding.reconcileFunding(current.attemptId as string, operation));
      if (isRecord(reconciled) && !reconciled.activeAction) {
        const currentActivityId = activityRef.current?.id;
        const confirmed = !!currentActivityId && hasVerifiedTransactionPhase(currentActivityId);
        operationProgress.finish(confirmed ? "confirmed" : "completed", confirmed
          ? "Funding transaction evidence was confirmed on chain."
          : "Saved funding status was checked.");
      }
    }
    catch (error) {
      if (isBindingCurrent(target)) {
        await refresh(target, current.attemptId);
        const statusLoaded = await loadFundingStatus(target, current.attemptId);
        const refreshed = statusLoaded ? await target.store.load(current.attemptId).catch(() => null) : null;
        const funding = isRecord(refreshed?.budgetFunding) ? refreshed.budgetFunding : null;
        const verifiedSettledStatus = funding !== null && funding.activeAction === null &&
          (funding.status === "intent" || funding.status === "committed" || funding.status === "returned");
        operationProgress.finish(verifiedSettledStatus ? "completed" : "needs_check", verifiedSettledStatus
          ? "A follow-up status check verified the saved funding state. No funding action remains pending."
          : fundingReconciliationMessage(error));
        return;
      }
    }
    if (isBindingCurrent(target)) { await refresh(target, current.attemptId); await loadFundingStatus(target, current.attemptId); }
  });

  const readFundingStatus = () => runAction("readFundingStatus", async (target, current, operationProgress) => {
    if (!current || typeof current.attemptId !== "string") return;
    operationProgress.stage("checking_funding_status");
    const loaded = await loadFundingStatus(target, current.attemptId);
    if (loaded) operationProgress.finish("completed", "The verified funding balance was refreshed.");
  });

  const refundFunding = () => runAction("refundFunding", async (target, current, operationProgress) => {
    if (!await loadRecovery(target, true)) return;
    if (!current || typeof current.attemptId !== "string") return;
    try {
      const refunded = await callOperation(target, (operation) => target.funding.refundFunding(current.attemptId as string, operation));
      if (isRecord(refunded) && refunded.status === "returned" && isRecord(refunded.returnedEvidence)) {
        operationProgress.finish("confirmed", "The refund was verified on chain.");
      }
    }
    catch { if (isBindingCurrent(target)) { await loadFundingStatus(target, current.attemptId); return; } }
    if (isBindingCurrent(target)) await loadFundingStatus(target, current.attemptId);
  });

  const setAcknowledged = (value: boolean) => {
    if (!binding || !review || !record || !isBindingCurrent(binding)) return;
    const key = value ? consentKey(review.consent) : null;
    updateFor(binding, (previous) => ({ ...previous, acknowledgedKey: key }));
  };
  const minimizeActivity = () => setActivityVisibility(false);
  const restoreActivity = () => setActivityVisibility(true);
  const dismissActivity = () => {
    const current = activityRef.current;
    if (!current || !binding || !isBindingCurrent(binding) || current.status === "running" || current.status === "submitted") return;
    activityRef.current = null;
    setActivity(null);
  };

  const visibleError = identityKey === "unavailable" ? null : state.error;
  return { record, activity: binding ? activity : null, review, acknowledged, restoring: binding ? state.restoring : identityKey !== "unavailable" && !state.restoreFailed,
    busy, error: visibleError, enabled, localAttempts: binding ? state.localAttempts : [],
    remoteReservations: binding ? state.remoteReservations : [], recoveryState: binding ? state.recoveryState : "unavailable",
    selectedAttemptId: binding ? state.selectedAttemptId : null, requestQuote, reviewQuote, approve: financialAction("approve"),
    purchase: financialAction("purchase"), reconcile, uploadNext, closeExpired, discardDraft,
    selectAttempt, startAnother, refreshRecovery,
    fundingConfigured: !!budgetVaultReference, fundingStatus: binding ? state.fundingStatus : null,
    prepareFunding, depositFunding, reconcileFunding, readFundingStatus, refundFunding, setAcknowledged, minimizeActivity, restoreActivity, dismissActivity };
}
